import React, { createContext, useContext } from "react";

/* ──────────────────────────────────────────────
   URL routing — maps page state to a shareable path.
   Direct hits like /consumer-terms-of-use/ are resolved
   on load; navigation pushes history entries so
   back / forward and refresh all keep working.
   Never change an existing legal path: the hosted
   payment pages link to them directly.
   ────────────────────────────────────────────── */
export const SITE_ORIGIN = "https://paybank2bank.io";

export const PAGE_TO_PATH = {
  home: "/",
  about: "/about-us/",
  legal: "/legal/",
  privacy: "/privacy-policy/",
  onboarding: "/onboarding-privacy-notice/",
  cookies: "/cookie-policy/",
  consumer: "/consumer-terms-of-use/",
  client: "/client-terms-of-use/",
  website: "/terms-of-use/",
  woocommerce: "/docs/woocommerce/",
  savings: "/savings-calculator/",
};

export const PAGE_TITLES = {
  home: "Paybank2bank — A2A Payments & Verification",
  about: "About us — Paybank2bank",
  legal: "Legal information — Paybank2bank",
  privacy: "Privacy policy — Paybank2bank",
  onboarding: "Merchant onboarding privacy notice — Paybank2bank",
  cookies: "Cookie policy — Paybank2bank",
  consumer: "Consumer terms of use — Paybank2bank",
  client: "Client terms of use — Paybank2bank",
  website: "Website terms of use — Paybank2bank",
  woocommerce: "WooCommerce plugin — Paybank2bank docs",
  savings: "Savings calculator — Paybank2bank",
};

export const PAGE_DESCRIPTIONS = {
  home: "Paybank2bank — Premium A2A payments and account verification powered by Open Banking. Faster, lower-cost bank payments with built-in risk intelligence.",
  about: "Paybank2bank is the trading name of Intense Technologies S.R.L., a Romanian technology company building Open Banking payments and account verification for businesses.",
  legal: "Company details, regulatory status and legal documents for Paybank2bank (Intense Technologies S.R.L.).",
  privacy: "How Paybank2bank collects, uses and shares personal data — for payers, account verification, business clients and website visitors.",
  onboarding: "How Paybank2bank uses personal data when a business applies through its self-service online application: the people named in the application, our checks, signing the agreement and card billing.",
  cookies: "The cookies and similar technologies used on the Paybank2bank website, payment pages and dashboard, and how to manage your choices.",
  consumer: "The terms that apply when you pay a business or verify your bank account using Paybank2bank's payment pages.",
  client: "Platform terms for businesses using Paybank2bank's API, dashboard, payment pages and plugins.",
  website: "The terms that apply when you use the paybank2bank.io website.",
  woocommerce: "Install and configure the Paybank2bank Pay by Bank plugin for WooCommerce.",
  savings: "Estimate how much your business could save by moving card payments to pay-by-bank.",
};

/* Pages rendered from the legal documents in src/legal */
export const LEGAL_PAGES = ["legal", "privacy", "onboarding", "cookies", "consumer", "client", "website"];

export const PATH_TO_PAGE = Object.entries(PAGE_TO_PATH).reduce((acc, [page, path]) => {
  acc[path] = page;
  if (path.endsWith("/") && path !== "/") acc[path.slice(0, -1)] = page;
  return acc;
}, {});

export const pageFromPath = (pathname) => PATH_TO_PAGE[pathname] || "home";

/* Navigation is provided by App: navigate(pageName, hash?) */
export const NavContext = createContext(null);

/* A real link (crawlable, opens in a new tab with a modifier key)
   that navigates inside the app on a plain left click. */
export function SiteLink({ to, hash, onClick, children, ...rest }) {
  const navigate = useContext(NavContext);
  const href = (PAGE_TO_PATH[to] || "/") + (hash ? `#${hash}` : "");

  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (!navigate || e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to, hash);
  };

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
